/**
 * Helper: General
 * -----------------------------------------------------------------------------
 * General helper utility functions.
 * - Functions which don't fit into any of the other utils files.
 * - Add in alphabetical order.
 *
 */

/**
 * Gets the formatted error message for maxQuantity limit errors.
 *
 * @param {string} maxQuantityType - The type of limit ('product' or 'variant').
 * @param {number} maxQuantityValue - The maximum quantity allowed.
 * @returns {string} The formatted error message.
 */
export function getMaxQuantityErrorMessage(maxQuantityType, maxQuantityValue) {
  const stringKey =
    maxQuantityType === 'product' ? 'text_product' : 'text_variant';
  const template =
    cnvs?.strings?.cart?.notifications?.max_quantity_limit?.[stringKey] || '';
  return template.replace('{{ maxQuantity }}', maxQuantityValue);
}

/**
 * Determines if a color is dark.
 * Supports hex strings (#RRGGBB or #RGB) and rgb() strings.
 *
 * @param {string} color - The CSS color string.
 * @returns {boolean} True if the color is dark, false otherwise.
 */
export function isColorDark(color) {
  let blue, green, red;

  // HEX format
  if (color.startsWith('#')) {
    let hex = color.slice(1);
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((ch) => ch + ch)
        .join('');
    }
    if (hex.length !== 6) {
      throw new Error('Invalid HEX color.');
    }
    red = parseInt(hex.substr(0, 2), 16);
    green = parseInt(hex.substr(2, 2), 16);
    blue = parseInt(hex.substr(4, 2), 16);
  } else if (color.startsWith('rgb')) {
    const nums = color.match(/\d+/g);
    if (!nums || nums.length < 3) {
      throw new Error('Invalid RGB color.');
    }
    [red, green, blue] = nums.map(Number);
  } else {
    throw new Error('Unsupported color format. Use #RRGGBB, #RGB, or rgb().');
  }

  // Perceived brightness formula
  const brightness = (red * 299 + green * 587 + blue * 114) / 1000;
  // Threshold of 128 is mid-point of [0…255]
  return brightness < 128;
}

/**
 * Debounce functions.
 * - Calls a function when a user hasn't carried out an event for a specific
 *   amount of time.
 * @param {Function} callback The function to debounce.
 * @param {Number} wait - The amount of time before debounce call is triggered.
 * @param {Boolean} immediate - Trigger the immediately.
 */
export function debounce(callback, wait, immediate) {
  let timeout = null;

  return function debounceFunc(...args) {
    const callNow = immediate && !timeout;

    function later() {
      timeout = null;
      if (!immediate) {
        callback.apply(this, args);
      }
    }

    window.clearTimeout(timeout);
    timeout = window.setTimeout(later, wait);

    if (callNow) {
      callback.apply(this, args);
    }
  };
}

/**
 * Handles pluralisation of strings based on Unicode plural rules.
 * - https://unicode-org.github.io/cldr-staging/charts/latest/supplemental/language_plural_rules.html
 * - Follows English plural rules.
 * - Zero + (One|Single) + (Two + Few) + (Other|Many|Plural).
 * @param {Object} strings - Strings object containing locale strings.
 * @param {Number} number - Number to use for plural rules.
 * @returns {String}
 */
export function handlePluralisation(strings, number) {
  if (strings.zero && number === 0) {
    return strings.zero;
  }

  const one = strings.one || strings.single;

  if (one && number === 1) {
    return one;
  }

  if (strings.two && number === 2) {
    return strings.two;
  }

  const many = strings.other || strings.many || strings.plural;

  if (strings.few) {
    if (many && strings.two && number === 3) {
      return strings.few;
    }

    if (!many && strings.two && number > 2) {
      return strings.few;
    }

    if (!many && number > 1) {
      return strings.few;
    }
  }

  return many;
}

/**
 * Test if a URL is a Shopify CDN URL.
 * - Returns true if URL contains:
 * -- cdn.shopify.com/
 * -- [custom-domain].[tld]/cdn/
 * -- [store].myshopify.com/cdn/
 * @param {String} url - URL to test.
 * @returns {Boolean}
 */
export function isCdnUrl(url) {
  if (!url) {
    return false;
  }

  const cdnUrls = [
    'cdn.shopify.com/',
    `${cnvs.store.shopDomain.replaceAll('/', '')}/cdn/`,
    `${cnvs.store.permanentDomain.replaceAll('/', '')}/cdn/`,
  ];

  return cdnUrls.some((cdnUrl) => url.includes(cdnUrl));
}

/**
 * Test if a part of an element is in the viewport.
 * @param {HTMLElement} element - Element to check.
 * @param {Object} [config] - Config options.
 * @param {HTMLElement} [config.container] - Container to check in.
 * @param {String} [config.direction] - Either `vertical` or `horizontal`.
 * @returns {Boolean} - True if element is at least partially in viewport.
 */
export function isVisible(element, { container, direction = 'vertical' } = {}) {
  let containerDimension = container
    ? container.offsetHeight
    : window.innerHeight;

  const elementRectangle = element.getBoundingClientRect();
  let elementStart = elementRectangle.top;
  let elementEnd = elementRectangle.bottom;

  if (direction === 'horizontal') {
    containerDimension = container ? container.offsetWidth : window.innerWidth;

    elementStart = elementRectangle.left;
    elementEnd = elementRectangle.right;
  }

  const startInView = elementStart >= 0 && elementStart < containerDimension;
  const endInView = elementEnd > 0 && elementEnd <= containerDimension;

  return startInView || endInView;
}

/**
 * Scrolls page smoothly to element.
 * @param {HTMLElement} element - Element to scroll to.
 * @param {Object} [config] - Additional data.
 * @param {Function} [config.callback] - Function run after completion.
 * @param {String} [config.headerState] - State of header when function is run.
 * @param {HTMLElement} [config.page] - Element to scroll.
 */
export function scrollToElement(
  element,
  { callback, headerState, page = window } = {},
) {
  let viewportFromPageTop = window.scrollY;

  if (page !== window) {
    viewportFromPageTop = 0;
  }

  /**
   * Get element scroll position.
   */
  const elementRectangle = element.getBoundingClientRect();
  const elementFromViewportTop = Math.floor(elementRectangle.top);
  let position = viewportFromPageTop + elementFromViewportTop;

  /**
   * Retrieve variables.
   */
  const headerHeight = Number(
    getComputedStyle(document.documentElement)
      .getPropertyValue('--header-height')
      .replace('px', ''),
  );

  const fontSize = Number(
    getComputedStyle(document.documentElement).fontSize.replace('px', ''),
  );

  let spacingM = Number(
    getComputedStyle(document.documentElement)
      .getPropertyValue('--spacing-m')
      .replace('rem', ''),
  );

  spacingM *= fontSize;

  /**
   * Offset height of header and update position.
   * - Assumes that when header is collapsed it has a height of 0.
   * - If it doesn't then update each condition with a new variable.
   * - Don't use --header-visible-height as that isn't updated in time.
   */
  const threshold =
    headerState === 'collapsed' ? spacingM : headerHeight + spacingM;

  if (elementFromViewportTop === threshold) {
    return;
  }

  const dynamicHeaderHeight =
    elementFromViewportTop > threshold ? 0 : headerHeight;

  position -= dynamicHeaderHeight;
  position -= spacingM;

  /**
   * Disable smooth scroll if prefers reduced motion.
   */
  const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 'auto'
    : 'smooth';

  page.scroll({
    behavior,
    left: 0,
    top: position,
  });

  if (typeof callback === 'function') {
    callback();
  }
}

/**
 * Set intersection observers to detect when add to cart button leaves viewport.
 * @param {Object} data - Data object.
 * @param {Object} data.callback - Function to run when intersecting.
 * @param {HTMLElement} data.element - Element to set observer on.
 * unobserved, must be a Vue element.
 * @param {String} [data.rootMargin] - Values to grow or shrink each side of the
 * root element's bounding box before computing intersections.
 * @param {Number|Array} [data.threshold] - Intersection observer threshold, 0.5
 * means the intersection is triggered when 50% of the element is in view.
 * @returns {IntersectionObserver}
 */
export function setIntersectionObserver({
  callback,
  element,
  rootMargin = '0px',
  threshold = 0.5,
}) {
  const options = {
    root: null,
    rootMargin,
    threshold,
  };

  const observer = new IntersectionObserver(callback, options);
  observer.observe(element);

  return observer;
}

/**
 * Throttle functions.
 * - Calls a function at intervals of a specified amount of time while the user
 *   is still carrying out the event.
 * @param {Function} callback - The function to execute when timer is passed.
 * @param {Number} wait - The amount of time before debounce call is triggered.
 * @param {Boolean} immediate - Trigger the immediately.
 */
export function throttle(callback, wait, immediate = false) {
  let timeout = null;
  let initialCall = true;

  return function throttleFunc(...args) {
    const callNow = immediate && initialCall;

    function next() {
      callback.apply(this, args);
      timeout = null;
    }

    if (callNow) {
      initialCall = false;
      next();
    }

    if (!timeout) {
      timeout = window.setTimeout(next, wait);
    }
  };
}

/**
 * Remove square brackets from a string.
 * @param {String} string - String to remove brackets from.
 * @returns {String} - Formatted string.
 */
export function removeBrackets(string) {
  return string.replace(/\[.*?\]/g, '').trim();
}

/**
 * Compute whether date is within start and end time.
 * - If value is 'false'/false then use fallback value, this adds support for no
 *   start/end dates.
 * - Uses YYYY-MM-DDTHH:MM as date time format.
 * @param {Boolean|String} startTime - Start date time format.
 * @param {Boolean|String} endTime - End date time format.
 * @returns {Boolean}
 */
export function dateTime(startTime, endTime) {
  const startDateTime =
    startTime === 'false' || !startTime
      ? new Date('2000-01-01T12:00')
      : new Date(startTime);

  const endDateTime =
    endTime === 'false' || !endTime
      ? new Date('2100-01-01T12:00')
      : new Date(endTime);

  const currentDateTime = new Date();

  /**
   * Check if the parsed dates are valid.
   */
  if (isNaN(startDateTime) || isNaN(endDateTime)) {
    return false;
  }

  /**
   * Check if the current date is within the specified range.
   */
  if (currentDateTime < startDateTime || currentDateTime > endDateTime) {
    return false;
  }

  return true;
}

/**
 * Wait for a window variable to be defined before proceeding with callback.
 * @example waitFor('nosto', callback)
 * @param {String} variable - Named window variable to wait for.
 * @param {Function} callback -Function to execute when interval is cleared.
 */
export function waitFor(variable, callback) {
  const timeout = 10000;
  const loop = 500;
  const start = performance.now();

  const interval = setInterval(() => {
    if (!window[variable]) {
      const difference = performance.now() - start;

      if (difference > timeout) {
        window.console.error(`Error: ${variable} not loaded.`);
        clearInterval(interval);
      }

      return;
    }

    clearInterval(interval);
    callback();
  }, loop);
}

/**
 * Enters fullscreen.
 */
export function enterFullscreen(video) {
  if (video.requestFullscreen) {
    video.requestFullscreen();
  } else if (video.mozRequestFullScreen) {
    video.mozRequestFullScreen();
  } else if (video.webkitRequestFullscreen) {
    video.webkitRequestFullscreen();
  } else if (video.webkitRequestFullScreen) {
    video.webkitRequestFullScreen();
  } else if (video.msRequestFullscreen) {
    video.msRequestFullscreen();
  } else if (video.webkitSupportsFullscreen) {
    video.webkitEnterFullscreen();
  }
}

/**
 * Shopify's Storefront API returns 'GB' as the ISO code for the United Kingdom
 * (e.g. cnvs.store.country), but internally — market metafields, warehouses,
 * variant availability lists — we standardise on 'UK'. Use this helper whenever
 * a Shopify country code is compared against our own market logic so the code
 * reads as 'UK' consistently.
 *
 * Keep the raw 'GB' value for anything sent back to Shopify (e.g. GraphQL
 * @inContext, whose CountryCode enum only accepts 'GB', never 'UK').
 *
 * @param {string} code - Country code from Shopify, f.e. cnvs.store.country.
 * @returns {string} Upper-cased, trimmed market code ('GB' -> 'UK').
 */
export function normalizeMarketCode(code) {
  if (!code) {
    return code;
  }

  const upperCode = code.toUpperCase().trim();
  return upperCode === 'GB' ? 'UK' : upperCode;
}

/**
 * @param {string} markets - String containing Markets, f.e. [DE, UK].
 * @param {string | undefined} keyword
 */
export function isAccessibleOnMarket(markets, keyword = null) {
  const hasBrackets = markets.includes('[') && markets.includes(']');
  const correctMarkets = markets.toUpperCase();
  const splitMarkets = correctMarkets.split('[').pop().split(']')[0].split(',');

  if (!correctMarkets || correctMarkets === keyword || !hasBrackets) {
    return true;
  }

  const countryCode = normalizeMarketCode(cnvs.store.country);
  const marketCode = normalizeMarketCode(cnvs.store.marketCode);

  for (const market of splitMarkets) {
    const trimmedCode = normalizeMarketCode(market);
    if (trimmedCode === countryCode || trimmedCode === marketCode) {
      return true;
    }
  }

  return false;
}

/**
 * Checks whether a Social Selling metaobject entry should be visible in the
 * customer's current market, based on its `only_show_in_selected_markets`
 * field value (comma-separated market codes). An empty value means the entry
 * is not market-restricted and is visible everywhere.
 *
 * @param {string | undefined} marketsValue - Comma-separated market codes, f.e. 'NL, DE'.
 * @returns {boolean} True if the entry should be visible in the current market.
 */
export function isVisibleInCurrentMarket(marketsValue) {
  if (!marketsValue?.trim()) {
    return true;
  }

  const markets = marketsValue
    .split(',')
    .map((code) => normalizeMarketCode(code.trim()));

  return markets.includes(normalizeMarketCode(cnvs.store.marketCode));
}

/**
 * Checks whether a market handle matches the shopper's current market.
 * - Centralizes campaign/freebie/price market matching, which previously
 *   compared against `cnvs.store.market` or `cnvs.store.marketName`
 *   inconsistently across call sites, with some lowercasing and others not.
 * - `cnvs.store.marketName` (not `cnvs.store.market`) is the canonical
 *   identifier for this comparison - it's the metafield campaign/freebie/
 *   price configuration is authored against.
 * @param {string | undefined} handle - Market handle to compare, e.g. a
 * campaign's configured market or a freebie/price's `marketHandle`.
 * @returns {boolean} True if handle matches the current market.
 */
export function isCurrentMarket(handle) {
  if (!handle) {
    return false;
  }

  return (
    handle.trim().toLowerCase() === cnvs.store.marketName?.trim().toLowerCase()
  );
}
